import React, { useState, useEffect, useRef } from 'react';
import { apiPost } from '../../api/api';

interface Props {
  itemType: 'event' | 'service' | 'group' | 'marketplace';
  currentText: string;
  onAccept: (text: string) => void;
}

const SYSTEM_INSTRUCTIONS =
  'Return ONLY the description text, ready to copy-paste. No prefixes like "Here is the refined description". Do not add any links or URLs unless they are already present in the input. Keep the response under 2000 characters.';

export default function AIDescriptionHelper({ itemType, currentText, onAccept }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'input' | 'response'>('input');
  const [userText, setUserText] = useState('');
  const [comments, setComments] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Sync currentText when modal opens
  useEffect(() => {
    if (open) {
      setUserText(currentText);
      setStep('input');
      setComments('');
      setAiResponse('');
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const callAI = async (text: string, instruction?: string) => {
    setLoading(true);
    try {
      let message = `${SYSTEM_INSTRUCTIONS} Please rephrase this ${itemType} description: "${text.trim()}"`;
      if (instruction?.trim()) message += ` Additional instructions: ${instruction.trim()}`;
      const data = await apiPost<{ response: string }>('/ai-chat', { message, request_type: 'description' }, true);
      if (data.response) {
        setAiResponse(data.response);
        setStep('response');
      }
    } catch (e) {
      console.error('AI error', e);
    } finally {
      setLoading(false);
    }
  };

  const handleImprove = () => callAI(userText, comments);

  const handleRefine = async () => {
    setLoading(true);
    try {
      let message = `${SYSTEM_INSTRUCTIONS} Please improve this ${itemType} description further: "${aiResponse.trim()}"`;
      if (comments.trim()) message += ` Additional instructions: ${comments.trim()}`;
      const data = await apiPost<{ response: string }>('/ai-chat', { message, request_type: 'description' }, true);
      if (data.response) setAiResponse(data.response);
    } catch (e) {
      console.error('AI refine error', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    onAccept(aiResponse);
    setOpen(false);
  };

  const taStyle: React.CSSProperties = {
    width: '100%', padding: '0.75rem', border: '1.5px solid #e5e7eb', borderRadius: '10px',
    fontSize: '0.9rem', color: '#111', fontFamily: 'inherit', resize: 'vertical',
    outline: 'none', boxSizing: 'border-box', backgroundColor: '#f9fafb',
  };

  return (
    <>
      {/* ✨ Use AI button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none',
          backgroundColor: '#FF385C', color: '#fff',
          fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(255,56,92,0.3)',
        }}
      >
        ✨ Use AI
      </button>

      {/* Modal overlay */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div ref={modalRef} style={{
            backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>✨</span>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#111' }}>AI Description Helper</span>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '1.25rem 1.5rem', flex: 1 }}>
              {step === 'input' ? (
                <>
                  <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Write your rough ideas or draft text, and I'll help make it more appealing:
                  </p>
                  <textarea
                    value={userText}
                    onChange={e => setUserText(e.target.value)}
                    placeholder={`Enter your ${itemType} ideas...`}
                    maxLength={2000}
                    rows={6}
                    style={taStyle}
                    onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  />
                  <textarea
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Optional: Add specific instructions (e.g. 'make it shorter', 'add paragraphs', 'more professional')"
                    rows={2}
                    style={{ ...taStyle, marginTop: '0.75rem', fontSize: '0.82rem' }}
                    onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  />
                  <button
                    onClick={handleImprove}
                    disabled={!userText.trim() || loading}
                    style={{
                      marginTop: '1rem', width: '100%', padding: '0.875rem',
                      backgroundColor: userText.trim() ? '#FF385C' : '#d1d5db',
                      border: 'none', borderRadius: '10px', color: '#fff',
                      fontWeight: 600, fontSize: '0.95rem',
                      cursor: userText.trim() && !loading ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                    }}
                  >
                    {loading ? <Spinner /> : <><span>✨</span> Improve Text</>}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Here's the improved version. You can edit it, add instructions, then refine or accept:
                  </p>
                  <textarea
                    value={aiResponse}
                    onChange={e => setAiResponse(e.target.value)}
                    rows={8}
                    maxLength={2000}
                    style={taStyle}
                    onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  />
                  <textarea
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Add instructions for refinement (e.g. 'make it shorter', 'more casual')"
                    rows={2}
                    style={{ ...taStyle, marginTop: '0.75rem', fontSize: '0.82rem' }}
                    onFocus={e => { e.currentTarget.style.borderColor = '#FF385C'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  />
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                    <button
                      onClick={handleRefine}
                      disabled={loading}
                      style={{
                        flex: 1, padding: '0.875rem', backgroundColor: '#f3f4f6',
                        border: 'none', borderRadius: '10px', color: '#374151',
                        fontWeight: 600, fontSize: '0.9rem', cursor: loading ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                      }}
                    >
                      {loading ? <Spinner dark /> : <><span>↺</span> Refine</>}
                    </button>
                    <button
                      onClick={handleAccept}
                      style={{
                        flex: 1, padding: '0.875rem', backgroundColor: '#FF385C',
                        border: 'none', borderRadius: '10px', color: '#fff',
                        fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                      }}
                    >
                      ✓ Accept
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Spinner({ dark }: { dark?: boolean }) {
  return (
    <div style={{
      width: '16px', height: '16px', border: `2px solid ${dark ? '#d1d5db' : 'rgba(255,255,255,0.4)'}`,
      borderTop: `2px solid ${dark ? '#374151' : '#fff'}`,
      borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    }} />
  );
}
